import { EventEmitter, Injectable, OnDestroy } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { Unit, UnitService } from '../../../helper/anessa-library/anessa-library/projects/unit-library/src/public-api';
import Utils from '../../../util';
import { ProjectFinancialService } from '../project-financial.service';
import { ProjectCommunicationService } from '../../../communication/project-comms.service';
import { CommunicationService } from '../../../communication/communication.service';
import { ProjectService } from '../../project.service';
import { take, takeUntil } from 'rxjs/operators';
import { FinancialOpex, FinancialOpexType } from '../../../models/project/financial/financial-opex.model';
import { EstimationItem } from '../../../models/data-structures/estimation-item.model';
import { ProgressPopupDialogComponent } from '../../progress-sidebar/progress-popup/progress-popup.component';
import { ProjectValidation, ValidationSectionName } from '../../../models/data-structures/project-validation';
import { MatDialog } from '@angular/material/dialog';
import { ConfiguredToastrService } from '../../../helper/toastr/configured-toastr.service';
import { ValidationService } from '../../../helper/validation/validation.service';
import { UnitValue } from '../../../models/data-structures/unit-value.model';
import { UnitValueValidation } from '../../../helper/validation/validation-sub-modules/unit-value.validation';
import { AnessaTranslationService } from '../../../helper/translation/anessa-translation.service';
import { PeriodType } from '../../../models/project/plant-specifications/fuel.model';
import { ProductType } from '../../../models/project/plant-specifications/product.model';

@Injectable({
  providedIn: 'root'
})
export class FinancialOpexService implements OnDestroy {
  onDestroyNotifier = new EventEmitter();
  opex: FinancialOpex[] = [];
  opexLoadedNotifier = new BehaviorSubject<boolean>(false);
  opexUpdatedNotifier = new BehaviorSubject<boolean>(false);
  unsavedChangesNotifier = new BehaviorSubject<boolean>(false);
  /**
   * The percentage of capex / opex cost;
   */
  opexPercentage: number;

  public savedTotalOpexUnit: string;
  /**
   * Array of default opex items.
   */
  public defaultItems: FinancialOpex[];
  /**
   * Array of default opex items converted to the current target units for opex.
   */
  private convertedDefaultItems: FinancialOpex[];

  /**
   * Boolean for is the financial service has been loaded.
   */
  private financialServiceLoaded = false;
  /**
   * Boolean for if the default items are ready to be displayed.
   */
  private defaultItemsReady = false;

  constructor(
    private financialService: ProjectFinancialService,
    private projectCommService: ProjectCommunicationService,
    private commService: CommunicationService,
    private projectService: ProjectService,
    private unitService: UnitService,
    private dialog: MatDialog,
    private toastrService: ConfiguredToastrService,
    private validationService: ValidationService,
    public translationService: AnessaTranslationService
  ) {
    this.financialService.financialFullyLoadedNotifier
      .pipe(takeUntil(this.onDestroyNotifier))
      .subscribe(loaded => {
        this.financialServiceLoaded = loaded;
        if (loaded) {
          this.opex = this.financialService.getOpex();
          this.financialService.initializeDefaultItemCalculation();
        } else {
          this.opex = [];
          this.savedTotalOpexUnit = null;
          this.defaultItems = null;
        }
        this.checkIfLoaded();
      });
    this.financialService.financialUpdatedNotifier
      .pipe(takeUntil(this.onDestroyNotifier))
      .subscribe(saved => {
        if (saved) {
          this.opex = this.financialService.getOpex();
          this.savedTotalOpexUnit = this.financialService.getSavedTotalOpexUnit();
          this.setOpexPercentage();
          this.opexUpdatedNotifier.next(true);
        }
      });
    this.financialService.defaultItemsReadyNotifier
      .pipe(takeUntil(this.onDestroyNotifier))
      .subscribe(ready => {
        this.defaultItemsReady = ready;
        if (ready && !this.projectCommService.isProjectInput()) {
          this.defaultItems = this.financialService.getDefaultItemsForOpex();
        } else {
          this.defaultItems = null;
        }
        this.checkIfLoaded();
      });
  }

  ngOnDestroy() {
    this.onDestroyNotifier.emit();
    this.onDestroyNotifier.unsubscribe();
  }

  getProjectPrecision() {
    return this.financialService.getProjectPrecision();
  }

  getOpex() {
    return this.opex;
  }

  getOpexItemByIndex(index: number) {
    if (this.opex) {
      if (index > -1 && index < this.opex.length) {
        return FinancialOpex.deepCopy(this.opex[index]);
      }
    }
  }

  addOpex(opex: FinancialOpex) {
    if (this.opex) {
      if (Utils.nameIsUnique(opex.name, this.opex)) {
        this.opex.push(opex);
        this.setOpexPercentage();
        this.opexUpdatedNotifier.next(true);
        this.unsavedChangesNotifier.next(true);
        return true;
      }
      this.toastrService.error(this.translationService.get('SERVICE.FINANCIAL_OPEX.OPEX_EXISTS_ERROR'), this.translationService.get('SERVICE.FINANCIAL_OPEX.OPEX_ADD_ERROR'));
      return false;
    }
    throw Error(this.translationService.get('SERVICE.FINANCIAL_OPEX.OPEX_SAVE_ERROR'));
  }

  getPeriodUnitTypeName(connectionType: string): string[] {
    switch (connectionType) {
      case PeriodType.PERIOD:
        return ['Period'];
      case PeriodType.ELECTRIC:
        return ['Energy'];
      case PeriodType.HEAT:
        return ['Thermal_Quantity'];
      case PeriodType.FEEDAMOUNT:
      case PeriodType.SOLID:
        return ['Mass'];
      case PeriodType.WHOLE:
      case PeriodType.THIN:
        return ['Mass', 'Volume_Liquid'];
      case PeriodType.BIOGAS:
        return ['Volume_Gas'];
      case PeriodType.BIOMETHANE:
        return ['Volume_Gas'];
    }
  }

  isConnectionTypeSafe(connectionType: string): boolean {
    const products = this.projectCommService.getProjectPlantSpecifications().generatedProducts;
    switch (connectionType) {
      case PeriodType.PERIOD:
      case PeriodType.FEEDAMOUNT:
      case PeriodType.BIOGAS:
        return true;
      case PeriodType.ELECTRIC:
        return products.some(prod => prod.type === ProductType.Electric);
      case PeriodType.HEAT:
        return products.some(prod => prod.type === ProductType.Heat);
      case PeriodType.SOLID:
        return products.some(prod => prod.type === ProductType.SolidDigestate);
      case PeriodType.WHOLE:
        return true;
      // return products.some(prod => prod.type === ProductType.WholeDigestate);
      case PeriodType.THIN:
        return products.some(prod => prod.type === ProductType.ThinDigestate);
      case PeriodType.BIOMETHANE:
        return products.some(prod => prod.type === ProductType.Biometh);
      default:
        return false;
    }
  }

  extractPeriodUnitValue(periodUnit: string, connectionType: string): string {
    const parsedPeriodUnit = Unit.parseUnitDisplayName(periodUnit);
    const periodUnitTypeNames = this.getPeriodUnitTypeName(connectionType);
    let retVal: string;

    for (const typeName of periodUnitTypeNames) {
      const unitId = this.unitService.getUnitByNameAndUnitTypeName(parsedPeriodUnit, typeName)?.id;
      if (unitId) {
        retVal = unitId;
        break;
      }
    }

    return retVal;
  }

  addOpexFromRow(row) {
    const opItem: FinancialOpex = new FinancialOpex();
    opItem.name = row['name'];
    opItem.description = row['description'];

    if (Object.values(FinancialOpexType)?.includes(row['type'])) {
      opItem.type = row['type'];
    } else {
      this.toastrService.error('invalid type for opex item ' + opItem.name)
    }

    if (this.isConnectionTypeSafe(row['connection'])) {
      opItem.connectionType = row['connection'];

      if (row['cost'] !== null && row['cost'] !== undefined) {
        opItem.cost = new UnitValue();
        opItem.cost.unit = this.unitService.getUnitByNameAndUnitTypeName(row['currency'], 'Currency')?.id;
        if (!opItem.cost.unit) {
          this.toastrService.error('failed to find cost unit for opex item ' + opItem.name);
        }
        opItem.cost.value = row['cost'];
      }

      if (row['period']) {
        if (opItem.type == FinancialOpexType.MAJOR_MAINT) {
          opItem.frequencyOfOccurrence = new UnitValue();
          opItem.frequencyOfOccurrence.unit = this.unitService.getUnitByNameAndUnitTypeName('years', 'Period').id;
          if (!opItem.frequencyOfOccurrence.unit) {
            this.toastrService.error('failed to find frequency of occurrence unit for opex item ' + opItem.name);
          }
          opItem.frequencyOfOccurrence.value = row['period'];
        } else {
          opItem.period = new UnitValue();
          opItem.period.unit = this.extractPeriodUnitValue(row['period_unit'], row['connection']);
          if (!opItem.period.unit) {
            this.toastrService.error('failed to find period unit for opex item ' + opItem.name);
          }
          opItem.period.value = row['period'];
        }
      }

    } else {
      this.toastrService.error('failed to find a product for connection type ' + row['connection']);
    }

    if (row['cpi'] !== null && row['cpi'] !== undefined) {
      opItem.cpi = new UnitValue();
      if (row['cpi_unit'] == '%') {
        opItem.cpi.unit = '000000000000000000002201';
      } else {
        const parsedCpiUnit = Unit.parseUnitDisplayName(row['cpi_unit']);
        opItem.cpi.unit = this.unitService.getUnitByNameAndUnitTypeName(parsedCpiUnit, 'CPI')?.id;
        if (!opItem.cpi.unit) {
          this.toastrService.error('failed to find cpi unit `' + row['cpi_unit'] + '` for opex item ' + opItem.name);
        }
      }
      opItem.cpi.value = row['cpi'];
    }

    this.addOpex(opItem);
  }

  updateOpexItem(opex: FinancialOpex, index: number) {
    if (this.opex && index > -1 && index < this.opex.length) {
      if (Utils.nameIsUnique(opex.name, this.opex, index)) {
        if (this.valuesChanged(opex, index)) {
          opex.estimated = false;
        }
        this.opex[index] = opex;
        this.setOpexPercentage();
        this.opexUpdatedNotifier.next(true);
        this.unsavedChangesNotifier.next(true);
        return true;
      }
      this.toastrService.error(this.translationService.get('SERVICE.FINANCIAL_OPEX.OPEX_EXISTS_ERROR'), this.translationService.get('SERVICE.FINANCIAL_OPEX.OPEX_ADD_ERROR'));
      return false;
    }
    throw Error(this.translationService.get('SERVICE.FINANCIAL_OPEX.OPEX_SAVE_ERROR'));
  }

  updateOpexItems(opexItems: FinancialOpex[]) {
    this.opex = opexItems;
    this.setOpexPercentage();
    this.opexUpdatedNotifier.next(true);
    this.unsavedChangesNotifier.next(true);
  }

  valuesChanged(opex, index) {
    const ignored = ['cpi', 'description', 'duration', 'estimated', 'frequencyOfOccurrence', 'type'];
    for (const prop in opex) {
      if (!ignored.includes(prop)) {
        if (opex[prop] instanceof UnitValue) {
          if (this.opex[index][prop].value !== opex[prop].value || this.opex[index][prop].unit !== opex[prop].unit) {
            return true;
          }
        } else if (this.opex[index][prop] !== opex[prop]) {
          return true;
        }
      }
    }
    return false;
  }

  removeOpex(index: number) {
    if (this.opex && index > -1 && index < this.opex.length) {
      this.opex.splice(index, 1);
      this.setOpexPercentage();
      this.opexUpdatedNotifier.next(true);
      this.unsavedChangesNotifier.next(true);
    }
  }

  saveOpex() {
    this.financialService.updateSaveTotalOpexUnit(this.savedTotalOpexUnit);
    this.financialService.saveOpex(this.opex);
    this.unsavedChangesNotifier.next(false);
  }

  resetOpex() {
    this.opex = this.financialService.getOpex();
    this.savedTotalOpexUnit = this.financialService.getSavedTotalOpexUnit();
    this.setOpexPercentage();
    this.opexUpdatedNotifier.next(true);
    this.unsavedChangesNotifier.next(true);
  }

  getTotalOpexInTargetCur() {
    const totalInTargetCur = new UnitValue();
    const totalInCad = this.getTotalOpexInCad();
    let targetCurUnit: Unit;
    targetCurUnit = this.financialService.getCurrencyUnitById(this.getTargetUnitForTotalOpex());
    if (!targetCurUnit) {
      throw Error('failed to find target unit for total opex');
    }
    totalInTargetCur.unit = targetCurUnit.id;
    totalInTargetCur.value = targetCurUnit.convertFromBase(totalInCad.value);
    return totalInTargetCur;
  }

  updateTotalOpexSavedUnit(unitId: string) {
    if (unitId !== this.savedTotalOpexUnit) {
      this.savedTotalOpexUnit = unitId;
      this.setConvertedDefaultItems();
      this.opexUpdatedNotifier.next(true);
      this.unsavedChangesNotifier.next(true);
    }
  }

  clearAll() {
    this.opex = [];
    this.opexPercentage = null;
    this.setOpexPercentage();
    this.opexUpdatedNotifier.next(true);
    this.unsavedChangesNotifier.next(true);
  }

  estimateOpex(opexPercentage: UnitValue) {
    const projectValidation = this.validationService.getProjectValidation();
    if (projectValidation.plantSpecificationsPercentage === 100
      && projectValidation.feedstockPercentage === 100
    ) {
      if (this.financialService.checkForBiomethOrElec()) {
        if (opexPercentage && opexPercentage.value && new UnitValueValidation(opexPercentage).isValid()) {
          this.commService.estimateCapex(opexPercentage.value * 0.01)
            .pipe(take(1)).subscribe(est => {
              if (est) this.sanitizeResults(est);
              if (est && Object.keys(est).includes('RESULTS.DATA.OPEX')) {
                this.opex = [];
                const opexEst = est['RESULTS.DATA.OPEX'];
                // opex is currently calculated in euro
                const euroUnitId = this.unitService.getUnitByName('EUR').id;
                const totalEstItem = new EstimationItem('RESULTS.DATA.TOTAL_OPEX', 0, euroUnitId, FinancialOpexType.OTHER);
                const isProjectInput = this.projectCommService.isProjectInput();
                for (const key of Object.keys(opexEst)) {
                  const estItem = new EstimationItem(key, opexEst[key]['RESULTS.DATA.VALUE'], euroUnitId, opexEst[key]['RESULTS.DATA.TYPE']);
                  const opexItem = this.createOpexItemFromEstItem(estItem);
                  // Combine all opex items when called from project input form
                  if (!isProjectInput) {
                    this.opex.push(opexItem);
                  } else {
                    totalEstItem.cost += estItem.cost;
                  }
                }
                if (isProjectInput) {
                  const totalOpex = this.createOpexItemFromEstItem(totalEstItem);
                  totalOpex.cpi.value = 0;
                  totalOpex.cpi.unit = '000000000000000000002201';
                  this.opex.push(totalOpex);
                  this.saveOpex();
                }
                this.setOpexPercentage();
                this.opexUpdatedNotifier.next(true);
                this.unsavedChangesNotifier.next(true);
              }
            });
        }
      } else {
        this.toastrService.warning(
          this.translationService.get('SERVICE.FINANCIAL_OPEX.NO_PRODUCT_ERROR'),
          this.translationService.get('SERVICE.FINANCIAL_OPEX.CANT_ESTIMATE_ERROR')
        );
      }
    } else {
      this.showProgPopup(projectValidation);
    }
  }

  private sanitizeResults(results) {
    // let copy = new Object(results);
    for (let key of Object.keys(results)) {
      let newKey = this.updateKey(results, key)
      if (results[newKey] && typeof results[newKey] === 'object') {
        let children = Object.keys(results[newKey]);
        if (children && children.length > 0) {
          results[newKey] = this.sanitizeResults(results[newKey]);
        }
      } else if (results[newKey] && typeof results[newKey] === 'string') {
        results[newKey] = results[newKey].replace(/~/g, '.');
      }
    }
    return results;
  }

  private updateKey(obj, oldKey) {
    const newKey = oldKey.replace(/~/g, '.');

    if (oldKey !== newKey) {
      Object.defineProperty(obj, newKey,
        Object.getOwnPropertyDescriptor(obj, oldKey));
      delete obj[oldKey];
    }
    return newKey;
  }

  /**
   * Get the value for opex percentage.
   */
  getOpexPercentage(): number {
    return this.opexPercentage;
  }

  /**
   * Get the list of default items that have been calculated for the current project.
   */
  getDefaultItems() {
    return this.convertedDefaultItems;
  }

  /**
   * Checks if all services are loaded and ready and triggers the opex service loaded notifier. If all service
   * are loaded total saved opex and opex percentage are calculated.
   */
  private checkIfLoaded() {
    if (this.financialServiceLoaded && this.defaultItemsReady) {
      this.savedTotalOpexUnit = this.financialService.getSavedTotalOpexUnit();
      this.setOpexPercentage();
      this.setConvertedDefaultItems();
    }
    this.opexLoadedNotifier.next(this.financialServiceLoaded && this.defaultItemsReady);
  }

  /**
   * Get the total cost, in CAD, of all opex items (including the default items). Major maintenance opex items are
   * not included because they are not setup on a per year basis.
   */
  private getTotalOpexInCad() {
    const totalInCad = new UnitValue({
      value: 0,
      unit: this.financialService.getUsdUnitId()
    });
    if (this.defaultItems) {
      for (const item of this.defaultItems) {
        const incomeUnit = this.financialService.getCurrencyUnitById(item.cost.unit);
        const incomeInCad = incomeUnit.convertToBase(item.cost.value);
        totalInCad.value += incomeInCad;
      }
    }
    if (this.opex) {
      for (const item of this.opex) {
        if (item && item.type !== FinancialOpexType.MAJOR_MAINT && new UnitValueValidation(item.cost).isValid() && new UnitValueValidation(item.period).isValid()) {
          const incomeUnit = this.financialService.getCurrencyUnitById(item.cost.unit);
          const incomeInCad = incomeUnit.convertToBase(item.cost.value);
          const valueInBase = this.unitService.getValueInBaseUnit(item.period);
          // Prevent division by zero error
          if (valueInBase > 0) {
            if (item.connectionType === PeriodType.PERIOD) {
              totalInCad.value += Utils.roundToPrecision(incomeInCad / (valueInBase / 365), 2);
            } else {
              this.financialService.incompleteConnection();
              totalInCad.value += incomeInCad * this.getAmountFor(item.connectionType) / valueInBase;
            }
          }
        } else if (item && item.type === FinancialOpexType.MAJOR_MAINT && new UnitValueValidation(item.cost).isValid() && new UnitValueValidation(item.frequencyOfOccurrence).isValid()) {
          const incomeUnit = this.financialService.getCurrencyUnitById(item.cost.unit);
          const incomeInCad = incomeUnit.convertToBase(item.cost.value);
          const valueInBase = this.unitService.getValueInBaseUnit(item.frequencyOfOccurrence);
          // Prevent division by zero error
          if (valueInBase > 0) {
            if (item.connectionType === PeriodType.PERIOD) {
              totalInCad.value += Utils.roundToPrecision(incomeInCad / (valueInBase / 365), 2);
            } else {
              totalInCad.value += incomeInCad * this.getAmountFor(item.connectionType) / valueInBase;
            }
          }
        }
      }
    }
    return totalInCad;
  }

  getTotalCapexInCAD() {
    return this.financialService.getTotalCapexAmountInCad();
  }

  /**
   * Returns the units id for the current target units for the opex total cost.
   */
  getTargetUnitForTotalOpex(): string {
    let targetUnit = '';
    targetUnit = this.savedTotalOpexUnit;
    if (!targetUnit) { // if saved unit is not avail service level
      targetUnit = this.financialService.getSavedTotalOpexUnit();
    }
    if (!targetUnit) { // if saved is null try this
      targetUnit = this.projectService.getDefaultCurrency();
    }
    if (!targetUnit) {// if default is null try this
      targetUnit = this.getFirstOpexUnit();
    }
    if (!targetUnit) { // if no first opex unit, try this
      targetUnit = this.financialService.getUsdUnitId(); // USD
    }
    return targetUnit;
  }

  private getFirstOpexUnit() {
    if (this.opex && this.opex.length > 0) {
      const firstOpex = this.opex[0];
      if (firstOpex.cost && firstOpex.cost.unit) {
        return firstOpex.cost.unit;
      }
    }
  }

  private convertOpexItemToTargetCur(item: FinancialOpex) {
    const targetUnit = this.financialService.getCurrencyUnitById(
      this.getTargetUnitForTotalOpex()
    );
    if (item && new UnitValueValidation(item.cost).isValid()) {
      const currentUnit = this.financialService.getCurrencyUnitById(item.cost.unit);
      const costInCad = currentUnit.convertToBase(item.cost.value);
      const costInTargetUnit = Utils.roundToPrecision(targetUnit.convertFromBase(costInCad), 2);
      item.cost = new UnitValue({ value: costInTargetUnit, unit: targetUnit.id });
    }
    return item;
  }

  private createOpexItemFromEstItem(estItem: EstimationItem) {
    let opexItem = new FinancialOpex();
    opexItem.name = this.translationService.get(estItem.name);
    opexItem.cost = new UnitValue({
      value: estItem.cost,
      unit: estItem.costUnit // as of now this is always in euro
    });
    opexItem.period = this.getDefaultOpexPeriod();
    opexItem.type = estItem.type;
    const defaults = this.projectCommService.getProjectDefaults();
    if (defaults && defaults.cpi) {
      opexItem.cpi = new UnitValue({ value: defaults.cpi.value, unit: defaults.cpi.unit });
    }
    opexItem = this.convertOpexItemToTargetCur(opexItem);
    opexItem.estimated = true;
    return opexItem;
  }

  private getDefaultOpexPeriod() {
    const yearUnit = this.unitService.getUnitByName('years').id;
    return new UnitValue({ value: 1, unit: yearUnit });
  }

  showProgPopup(projectValidation: ProjectValidation) {
    // TODO this is temp, push to array and implement prop pop up data in to be done properly (ask Kyle)
    // they only show up one at a time right now, they should be all be shown together
    let section = ValidationSectionName.All;
    let warning: string;
    if (projectValidation.plantSpecificationsPercentage !== 100) {
      section = ValidationSectionName.PlantSpecifications;
      warning = this.translationService.get('SERVICE.FINANCIAL_OPEX.INVALID_PLANT_SPEC');
    } else if (projectValidation.feedstockPercentage !== 100) {
      if (this.validationService.checkFeedstockCharacteristics()) {
        this.toastrService.warning(this.translationService.get('SERVICE.FINANCIAL_OPEX.MISSING_CHARACTERISTICS'));
      }
      section = ValidationSectionName.Feedstock;
      warning = this.translationService.get('SERVICE.FINANCIAL_OPEX.INVALID_FEEDSTOCK');
    }
    if (warning) {
      this.toastrService.warning(warning);
    }
    this.dialog.open(ProgressPopupDialogComponent, {
      width: '800px',
      height: '85vh',
      data: {
        projectValidation: projectValidation,
        section: section,
        onlyShowInvalid: true
      }
    });
  }

  /**
   * Set the current opex percentage. Gets both total value of opex and capex in CAD
   * and performs the calculation of capex / opex to set the value
   */
  private setOpexPercentage(): UnitValue {
    if (this.financialService && this.financialService.financialLoadedNotifier.value && this.financialService.currencyUnitsNotifier.value) {
      const capexAmount: UnitValue = this.financialService.getTotalCapexAmountInCad(this.financialService.getCapex());
      const opexAmount: UnitValue = this.getTotalOpexInCad();
      if (capexAmount && opexAmount && capexAmount.value > 0) {
        const capexValue = capexAmount.value;
        const opexValue = opexAmount.value;
        this.opexPercentage = (opexValue / capexValue) * 100;
        return;
      }
    }
    this.opexPercentage = null;
  }

  /**
   * Converts the default opex items from CAD to the target units for the opex total.
   */
  private setConvertedDefaultItems() {
    if (this.defaultItemsReady && this.financialServiceLoaded && this.defaultItems) {
      this.convertedDefaultItems = [];
      const targetUnitId = this.getTargetUnitForTotalOpex();
      const targetUnit = this.unitService.getUnitById(targetUnitId);
      for (const item of this.defaultItems) {
        const convertedItem = new FinancialOpex(item);
        const convertedCost = new UnitValue({ value: targetUnit.convertFromBase(item.cost.value), unit: targetUnitId });

        convertedCost.unit = targetUnit.id;
        convertedItem.cost = convertedCost;
        this.convertedDefaultItems.push(convertedItem);
      }
    }
  }

  public getAmountFor(type: string) {
    const rates = this.financialService.getRates();
    if (rates && rates[type]) {
      return rates[type] * 365;
    }
    return 0;
  }
}
